import { jsPDF } from "jspdf";
import { mandateDimensions, type WeightMap } from "@/data/mandateScorecard";

type ScoreValue = { score?: number; note?: string };
type Person = { id: string; name: string };
type LogisticsState = "not-checked" | "pass" | "fail";

export type CandidatePdfInput = {
  roleTitle: string;
  candidate: Person;
  interviewers: Person[];
  scores: Record<string, ScoreValue>;
  logistics: Record<string, LogisticsState[]>;
  logisticsLabels: string[];
  mandateLabel: string;
  aiRoleLabel: string;
  weights: WeightMap;
  date?: Date;
};

export type CandidatePdfModel = {
  roleTitle: string;
  candidateName: string;
  date: string;
  mandateLabel: string;
  aiRoleLabel: string;
  interviewerNames: string[];
  dimensions: Array<{
    title: string;
    scores: Array<number | null>;
    average: number | null;
    spread: number | null;
    flag: boolean;
    notes: Array<{ interviewer: string; note: string }>;
    weight: number;
  }>;
  weightedTotals: Array<{ interviewer: string; value: number | null; scored: number; provisional: boolean }>;
  weightedAverage: number | null;
  provisional: boolean;
  logistics: Array<{ interviewer: string; states: Array<{ label: string; state: LogisticsState }> }>;
};

const key = (candidateId: string, interviewerId: string, dimensionId: string) => `${candidateId}:${interviewerId}:${dimensionId}`;
const logisticsKey = (candidateId: string, interviewerId: string) => `${candidateId}:${interviewerId}`;

function dateLabel(date: Date) {
  return date.toISOString().slice(0, 10);
}

function average(values: number[]) {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
}

function weightedTotal(candidateId: string, interviewerId: string, scores: Record<string, ScoreValue>, weights: WeightMap) {
  const scored = mandateDimensions.filter((dimension) => typeof scores[key(candidateId, interviewerId, dimension.id)]?.score === "number");
  if (!scored.length) return { value: null, scored: 0, provisional: true };
  const weightSum = scored.reduce((sum, dimension) => sum + (weights[dimension.id] ?? 0), 0);
  const value = scored.reduce((sum, dimension) => sum + ((scores[key(candidateId, interviewerId, dimension.id)]?.score ?? 0) / 5) * (weights[dimension.id] ?? 0), 0);
  return { value: weightSum ? (value / weightSum) * 100 : null, scored: scored.length, provisional: scored.length < mandateDimensions.length };
}

export function buildCandidatePdfModel(input: CandidatePdfInput): CandidatePdfModel {
  const dimensions = mandateDimensions.map((dimension) => {
    const scores = input.interviewers.map((interviewer) => input.scores[key(input.candidate.id, interviewer.id, dimension.id)]?.score ?? null);
    const values = scores.filter((score): score is number => typeof score === "number");
    return {
      title: dimension.title,
      scores,
      average: average(values),
      spread: values.length >= 2 ? Math.max(...values) - Math.min(...values) : null,
      flag: values.includes(1),
      notes: input.interviewers.map((interviewer, index) => ({ interviewer: interviewer.name, note: input.scores[key(input.candidate.id, interviewer.id, dimension.id)]?.note ?? "" })).filter(({ note }) => note.trim()),
      weight: input.weights[dimension.id] ?? 0,
    };
  });
  const weightedTotals = input.interviewers.map((interviewer) => ({ interviewer: interviewer.name, ...weightedTotal(input.candidate.id, interviewer.id, input.scores, input.weights) }));
  const values = weightedTotals.map((total) => total.value).filter((value): value is number => typeof value === "number");
  const counts = weightedTotals.map((total) => total.scored).filter((count) => count > 0);
  return {
    roleTitle: input.roleTitle,
    candidateName: input.candidate.name,
    date: dateLabel(input.date ?? new Date()),
    mandateLabel: input.mandateLabel,
    aiRoleLabel: input.aiRoleLabel,
    interviewerNames: input.interviewers.map((interviewer) => interviewer.name),
    dimensions,
    weightedTotals,
    weightedAverage: average(values),
    provisional: counts.some((count) => count < mandateDimensions.length),
    logistics: input.interviewers.map((interviewer) => ({ interviewer: interviewer.name, states: input.logisticsLabels.map((label, index) => ({ label, state: input.logistics[logisticsKey(input.candidate.id, interviewer.id)]?.[index] ?? "not-checked" })) })),
  };
}

function safeFilenamePart(value: string) {
  return value.trim().replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "untitled";
}

export function scorecardFilename(input: Pick<CandidatePdfInput, "roleTitle" | "candidate" | "date">) {
  return `scorecard-${safeFilenamePart(input.roleTitle)}-${safeFilenamePart(input.candidate.name)}-${dateLabel(input.date ?? new Date())}.pdf`;
}

function writeWrapped(doc: jsPDF, text: string, x: number, y: number, width: number, lineHeight = 4.2) {
  const lines = doc.splitTextToSize(text, width) as string[];
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

function addFooter(doc: jsPDF, pageWidth: number, pageHeight: number) {
  doc.setDrawColor(223, 230, 223);
  doc.line(16, pageHeight - 16, pageWidth - 16, pageHeight - 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(69, 96, 82);
  doc.text("Alder Search | aldersearch.com", 16, pageHeight - 10);
  doc.text("Use the total to start the conversation, not to end it.", pageWidth - 16, pageHeight - 10, { align: "right" });
}

function addCandidatePage(doc: jsPDF, model: CandidatePdfModel, firstPage: boolean) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = 16;
  const right = pageWidth - 16;
  const contentWidth = right - left;
  let y = 18;
  const ensureSpace = (needed: number) => {
    if (y + needed <= pageHeight - 23) return;
    addFooter(doc, pageWidth, pageHeight);
    doc.addPage();
    y = 18;
  };
  if (!firstPage) y = 18;
  doc.setTextColor(23, 61, 45);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(143, 116, 65);
  doc.text("ALDER SEARCH | 8-DIMENSION MANDATE SCORECARD", left, y);
  y += 9;
  doc.setTextColor(23, 61, 45);
  doc.setFontSize(20);
  doc.text(model.candidateName, left, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(69, 96, 82);
  y = writeWrapped(doc, `${model.roleTitle}  |  ${model.date}`, left, y, contentWidth);
  y += 5;
  doc.setTextColor(23, 61, 45);
  doc.setFont("helvetica", "bold");
  doc.text("Mandate", left, y);
  doc.setFont("helvetica", "normal");
  doc.text(model.mandateLabel, left + 25, y);
  doc.setFont("helvetica", "bold");
  doc.text("AI role", left + 82, y);
  doc.setFont("helvetica", "normal");
  doc.text(model.aiRoleLabel, left + 103, y);
  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("Interviewers", left, y);
  doc.setFont("helvetica", "normal");
  y = writeWrapped(doc, model.interviewerNames.join(", ") || "None recorded", left + 25, y, contentWidth - 25);
  y += 5;
  doc.setDrawColor(143, 116, 65);
  doc.line(left, y, right, y);
  y += 7;

  ensureSpace(18);
  doc.setFillColor(23, 61, 45);
  doc.rect(left, y - 4, contentWidth, 8, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("DIMENSION", left + 2, y + 1);
  const scoreStart = left + 59;
  model.interviewerNames.forEach((name, index) => doc.text(name.slice(0, 16), scoreStart + index * 22, y + 1));
  const averageX = scoreStart + model.interviewerNames.length * 22;
  doc.text("AVG", averageX, y + 1);
  doc.text("SPREAD", averageX + 17, y + 1);
  doc.text("WEIGHT", right - 15, y + 1, { align: "right" });
  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(23, 61, 45);
  doc.setFontSize(8);
  model.dimensions.forEach((dimension) => {
    ensureSpace(12);
    if (dimension.flag) { doc.setFillColor(255, 247, 247); doc.rect(left, y - 4, contentWidth, 9, "F"); }
    doc.setFont("helvetica", "bold");
    doc.text(`${dimension.title}${dimension.flag ? "  FLAG" : ""}`, left + 2, y + 1);
    doc.setFont("helvetica", "normal");
    dimension.scores.forEach((score, index) => doc.text(score === null ? "Not scored" : String(score), scoreStart + index * 22, y + 1));
    doc.text(dimension.average === null ? "Not scored" : dimension.average.toFixed(1), averageX, y + 1);
    doc.text(dimension.spread === null ? "-" : String(dimension.spread), averageX + 17, y + 1);
    doc.text(`${dimension.weight}%`, right - 15, y + 1, { align: "right" });
    y += 7;
    doc.setDrawColor(232, 236, 232);
    doc.line(left, y, right, y);
  });
  ensureSpace(20);
  y += 5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Weighted total out of 100", left, y);
  const totalText = model.weightedAverage === null ? "Not scored" : `${model.weightedAverage.toFixed(1)}/100${model.provisional ? " provisional" : ""}`;
  doc.text(totalText, right, y, { align: "right" });
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(69, 96, 82);
  const interviewerTotals = model.weightedTotals.map((total) => `${total.interviewer}: ${total.value === null ? "Not scored" : `${total.value.toFixed(1)}/100${total.provisional ? " provisional" : ""}`}`).join("  |  ");
  y = writeWrapped(doc, interviewerTotals || "No interviewer totals", left, y, contentWidth, 3.8);
  y += 2;
  doc.text("Weights used are shown in the table. A score of 1 on any dimension is a flag.", left, y);
  y += 8;
  ensureSpace(20);
  doc.setTextColor(23, 61, 45);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Logistics", left, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  model.logistics.forEach((entry) => {
    ensureSpace(10);
    const status = entry.states.map(({ label, state }) => `${label}: ${state === "not-checked" ? "Not checked" : state === "pass" ? "Pass" : "Fail"}`).join("  |  ");
    y = writeWrapped(doc, `${entry.interviewer}: ${status}`, left, y, contentWidth, 3.8);
    y += 2;
  });
  y += 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Interview notes", left, y);
  y += 6;
  model.dimensions.forEach((dimension) => {
    dimension.notes.forEach(({ interviewer, note }) => {
      ensureSpace(18);
      doc.setTextColor(143, 116, 65);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text(`${dimension.title} | ${interviewer}`, left, y);
      y += 4;
      doc.setTextColor(69, 96, 82);
      doc.setFont("helvetica", "normal");
      y = writeWrapped(doc, note, left, y, contentWidth, 3.8);
      y += 4;
    });
  });
  addFooter(doc, pageWidth, pageHeight);
}

export function createScorecardPdf(inputs: CandidatePdfInput[]) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  inputs.forEach((input, index) => {
    if (index > 0) doc.addPage();
    addCandidatePage(doc, buildCandidatePdfModel(input), index === 0);
  });
  return doc;
}

export function downloadScorecardPdf(inputs: CandidatePdfInput[], filename: string) {
  createScorecardPdf(inputs).save(filename);
}
